import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Button, Dropdown, DropdownProps, Form } from 'semantic-ui-react';
import Datetime from 'react-datetime';
import { UseExternalFilterResult } from 'utils/agGrid/hooks/useExternalFilter';
import moment from 'moment';
import ProductsDropdown from 'common/forms/ProductsDropdown';
import { Moment } from 'moment';
import ExchangeSymbolDropdown from 'common/forms/ExchangeSymbolDropdown';

export const Wrapper = styled.div`
  padding: 0 !important;
  margin: 0 0 0 6px !important;

  .fields {
    flex-wrap: wrap;
    align-items: flex-end;
  }
`;

type Props = Pick<
  UseExternalFilterResult,
  | 'setExternalFilter'
  | 'getFilterValue'
  | 'setFilterFromEvent'
  | 'hasFiltersApplied'
  | 'clearFilters'
>;

const ExternalFilters = ({
  setExternalFilter,
  getFilterValue,
  hasFiltersApplied,
  clearFilters,
}: Props) => {
  const onDateChange = useCallback(
    (time: Moment) => {
      setExternalFilter({
        colId: 'tradeDate',
        value: moment(time).format('YYYY-MM-DD'),
        op: 'equals',
      });
    },
    [setExternalFilter],
  );

  const onTimestampChange = useCallback(
    (time: Moment) => {
      const value = moment(time).format('YYYY-MM-DD HH:mm:ss');
      setExternalFilter({
        colId: 'timestamp',
        value,
        op: 'equals',
      });
    },
    [setExternalFilter],
  );

  const onProductChange = useCallback(
    (_: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
      if (value !== undefined)
        setExternalFilter({
          colId: 'productSymbol',
          value,
          op: 'equals',
        });
    },
    [setExternalFilter],
  );

  const onContractChange = useCallback(
    (_: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
      if (value !== undefined)
        setExternalFilter({
          colId: 'contractSymbol',
          value,
          op: 'equals',
        });
    },
    [setExternalFilter],
  );

  const selectedProduct = useMemo(
    () => getFilterValue('productSymbol'),
    [getFilterValue],
  );

  const creationTypeOptions = useMemo(
    () => [
      { key: 1, text: 'Intraday', value: 'intraday' },
      { key: 2, text: 'End of Day', value: 'end_of_day' },
    ],
    [],
  );


  return (
    <Wrapper>
      <Form>
        <Form.Group>
          <Form.Field
            control={Datetime}
            className="ui input datetime"
            label="Date"
            name="tradeDate"
            id="tradeDate"
            defaultValue=""
            dateFormat="YYYY-MM-DD HH:mm:ss"
            timeFormat
            value={getFilterValue('tradeDate')}
            inputProps={{ placeholder: 'Filter Date' }}
            onChange={onDateChange}
            closeOnSelect
          />
          <ProductsDropdown
            onChange={onProductChange}
            value={getFilterValue('productSymbol')}
          />
          <ExchangeSymbolDropdown
            onChange={onContractChange}
            value={getFilterValue('contractSymbol')}
            productSymbol={selectedProduct}
          />
          <Form.Field
            label={'Settlement Type'}
            name={'creationType'}
            id={'creationType'}
            placeholder={'Select Settlement Type'}
            control={Dropdown}
            options={creationTypeOptions}
            selection
            clearable
            onChange={(
              _: React.SyntheticEvent<HTMLElement>,
              { value }: DropdownProps,
            ) => {
              if (value !== undefined)
                setExternalFilter({
                  colId: 'creationType',
                  op: 'equals',
                  value,
                });
            }}
            value={getFilterValue('creationType')}
          />
          <Form.Field
            control={Datetime}
            className="ui input datetime"
            label="Timestamp"
            name="timestamp"
            id="timestamp"
            defaultValue=""
            dateFormat="YYYY-MM-DD"
            timeFormat="HH:mm:ss"
            value={getFilterValue('timestamp')}
            inputProps={{ placeholder: 'Filter Timestamp' }}
            onChange={onTimestampChange}
            closeOnSelect
          />
          <Button
            color="red"
            disabled={!hasFiltersApplied}
            id="clear-filters"
            content="Clear"
            onClick={clearFilters}
            style={{ height: '38px', marginLeft: '1em' }}
          />
        </Form.Group>
      </Form>
    </Wrapper>
  );
};

export default ExternalFilters;
